import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MAT_SNACK_BAR_DATA, MatSnackBarRef } from '@angular/material/snack-bar';
import { AuraColorType, UiThemeUtils } from '../../../utils/ui-theme.utils';

export interface AlertData {
  title?: string;
  message?: string;
  type: AuraColorType;
}

@Component({
  selector: 'aura-alert',
  templateUrl: './alert.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, MatButtonModule, NgClass],
  host: {
    class: 'block w-full',
  },
})
export class AlertAuraComponent {
  // External inputs (read-only signals)
  alertInput = input<AlertData>();
  dismissibleInput = input<boolean>(false);
  showIconInput = input<boolean>(true);

  // Internal writable signals (for fallback / programmatic set)
  alert = signal<AlertData | undefined>(undefined);
  dismissible = signal<boolean>(false);
  showIcon = signal<boolean>(true);

  dismissed = signal(false);
  readonly auraColorType = AuraColorType;

  private readonly data = inject(MAT_SNACK_BAR_DATA, {
    optional: true,
  }) as { alert: AlertData; dismissible: boolean; showIcon: boolean } | undefined;

  private readonly snackBarRef = inject(MatSnackBarRef<AlertAuraComponent>, {
    optional: true,
  });

  readonly isSnackBar = computed(() => !!this.snackBarRef);

  readonly effectiveAlert = computed(() => {
    return this.data?.alert ?? this.alert() ?? this.alertInput();
  });

  readonly effectiveDismissible = computed(() => {
    return this.data?.dismissible ?? this.dismissible() ?? this.dismissibleInput();
  });

  readonly effectiveShowIcon = computed(() => {
    return this.data?.showIcon ?? this.showIcon() ?? this.showIconInput();
  });

  readonly alertClass = computed(() => {
    return UiThemeUtils.getAuraTheme(this.effectiveAlert()?.type);
  });

  dismiss() {
    this.dismissed.set(true);
    if (this.snackBarRef) {
      this.snackBarRef.dismiss();
    }
  }
}
